import { api } from '@/api';
import {
  instanceOfBeamConstancyWorkResultDto,
  JobOccurrenceDto,
  ResultState,
  WorkState,
  ResultDto,
  UpdateStateDto,
  UpdateStateDtoTypeEnum,
  WorkItemDto,
  JobState,
} from '@/api/generator';
import { Measurement, MeasurementEssentials } from '@/components/measurement/types';
import { useFormValues, useResetForm } from 'vee-validate';
import { computed, Ref } from 'vue';
import { toUpdateUpdateWorkStepDto } from '../mapping';
import { WorkStepDto } from '@/api/generator/models/WorkStepDto';

export function useJobOccurrence() {
  const jobOccurrence = useFormValues<JobOccurrenceDto>();

  const reset = useResetForm<Partial<JobOccurrenceDto>>();

  const workitems = computed(() => jobOccurrence.value.workItems);

  const targetSystem = computed(() => jobOccurrence.value.targetSystem);

  const supportSystems = computed(() => jobOccurrence.value.supportSystems);

  async function reload() {
    if (jobOccurrence.value.id) {
      const { result: values } = await api.jobOccurrencesApi.getJobOccurrence({
        id: jobOccurrence.value.id,
      });
      if (values) {
        reset({ values }, { force: true });
      }
    }
  }

  function inAnyStateOf(JobStates: JobState[] | undefined) {
    return !!JobStates?.some((s) => s === jobOccurrence.value.state);
  }

  function getWorkStepForMeasurement(measurement: MeasurementEssentials): WorkStepDto | undefined {
    const hash = measurement.request.parameterCombinationHash;

    return jobOccurrence.value.workItems
      ?.flatMap<WorkStepDto>((workitem) => workitem.workSteps ?? [])
      .find((workstep) => workstep.combination?.hash === hash);
  }

  function getResultForMeasurement(measurement: MeasurementEssentials) {
    const workstep = measurement ? getWorkStepForMeasurement(measurement) : undefined;

    return workstep?.results
      ?.filter(instanceOfBeamConstancyWorkResultDto)
      .find((r) => r.measurementId === measurement?.measurementId);
  }

  function isWorkStepSkipable(workstep: WorkStepDto) {
    return (
      (workstep?.workState === WorkState.Open && !workstep?.results?.length) ||
      workstep?.workState === WorkState.Skipped
    );
  }

  function isSkippAllPossible(workitem: WorkItemDto) {
    return !workitem.workSteps?.some((workstep) => workstep.results?.length);
  }

  function areAllWorkstepsSkipped(workitem: WorkItemDto) {
    return workitem.workSteps?.every((workstep) => workstep.workState === 'Skipped');
  }

  async function toggleSkipAllWorksteps(workitem: WorkItemDto, skip?: boolean) {
    if (jobOccurrence.value.id === undefined) {
      throw new Error('jobOccurence.id should always be defined when skipAll is called');
    }

    const { success } = await api.jobOccurrencesApi.skipAllWorkItems({
      id: jobOccurrence.value.id,
      workItemID: workitem.id,
      skipAll: skip ?? !areAllWorkstepsSkipped(workitem),
    });

    await reload();

    return success;
  }

  async function toggleSkipWorkstep(workstep: WorkStepDto, skip?: boolean) {
    if (!jobOccurrence.value.id) {
      throw new Error('JobOccurrence id is required');
    }

    const skipped = workstep.workState === WorkState.Skipped;

    const newSkipped = skip ?? !skipped;

    if (isWorkStepSkipable(workstep)) {
      const { success } = await api.jobOccurrencesApi.updateWorkStep({
        id: jobOccurrence.value.id,
        workStepId: workstep.id,
        updateWorkStepDto: {
          ...toUpdateUpdateWorkStepDto(workstep),
          workState: newSkipped ? WorkState.Skipped : WorkState.Open,
        },
      });

      if (success) {
        await reload();
      }
    }
  }

  async function toggleSkipMeasurement(measurement: Measurement) {
    const workstep = getWorkStepForMeasurement(measurement);

    if (workstep) {
      return toggleSkipWorkstep(workstep);
    }
  }

  function isMeasurementSkipped(measurement: Measurement): Ref<boolean> {
    return computed(() => {
      const workstep = getWorkStepForMeasurement(measurement);

      return workstep?.workState === 'Skipped';
    });
  }
  async function discardResult(result: ResultDto) {
    if (result.id) {
      const { success } = await api.resultsApi.updateResult({
        id: result.id,
        updateResultDto: {
          $type:
            UpdateStateDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoUpdateStateDtoPtwPtw100AppZeroApplicationShared,
          state: 'Discarded',
        } satisfies UpdateStateDto,
      });

      if (success) {
        await reload();
      }
    }
  }

  async function discardMeasurement(measurement?: Measurement) {
    if (measurement?.measurementId) {
      const result = getResultForMeasurement(measurement);

      if (result && instanceOfBeamConstancyWorkResultDto(result)) {
        discardResult(result);
      }
    }
  }

  async function toggleIgnoreResult(result: ResultDto) {
    let state: ResultState | undefined = undefined;
    switch (result.state) {
      case 'Accepted':
        state = ResultState.Ignored;
        break;
      case 'Ignored':
        state = ResultState.Accepted;
        break;
      default:
        state = undefined;
    }

    if (result.id && state) {
      const { success } = await api.resultsApi.updateResult({
        id: result.id,
        updateResultDto: {
          $type:
            UpdateStateDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoUpdateStateDtoPtwPtw100AppZeroApplicationShared,
          state,
        },
      });

      if (success) {
        await reload();
      }
    }
  }

  return {
    jobOccurrence,
    inAnyStateOf,
    targetSystem,
    supportSystems,
    workitems,
    reload,
    toggleSkipWorkstep,
    toggleSkipMeasurement,
    toggleSkipAllWorksteps,
    areAllWorkstepsSkipped,
    isMeasurementSkipped,
    isSkippAllPossible,
    getWorkStepForMeasurement,
    toggleIgnoreResult,
    getResultForMeasurement,
    discardResult,
    discardMeasurement,
    isWorkStepSkipable,
  };
}
